import { z } from "zod";

const PHONE_E164_REGEX = /^\+[1-9]\d{1,14}$/;

const emptyStringToUndefined = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

const isLikelyPhoneE164 = (value: string): boolean =>
  PHONE_E164_REGEX.test(value.trim());

export type AcceptInvitationFormValues = {
  password: string;
  confirmPassword: string;
  acceptLegal: boolean;
  person: {
    displayName: string;
    preferredName: string;
    mobilePhoneE164: string;
    canAppearInPhoto: boolean;
  };
};

export const initialAcceptInvitationFormValues: AcceptInvitationFormValues = {
  password: "",
  confirmPassword: "",
  acceptLegal: false,
  person: {
    displayName: "",
    preferredName: "",
    mobilePhoneE164: "",
    canAppearInPhoto: false,
  },
};

export type AcceptInvitationLegalVersions = {
  acceptedUserTermsVersion: string;
  acceptedPrivacyVersion: string;
};

export function buildAcceptInvitationSchema(
  requiresPerson: boolean,
  legalVersions: AcceptInvitationLegalVersions,
) {
  return z
    .object({
      password: z.string().min(12, "Bitte verwenden Sie mindestens 12 Zeichen."),
      confirmPassword: z.string(),
      acceptLegal: z.boolean(),
      person: z.object({
        displayName: z.string(),
        preferredName: z.string(),
        mobilePhoneE164: z.string(),
        canAppearInPhoto: z.boolean(),
      }),
    })
    .superRefine((value, ctx) => {
      if (value.password !== value.confirmPassword) {
        ctx.addIssue({
          code: "custom",
          path: ["confirmPassword"],
          message: "Die Passwörter stimmen nicht überein.",
        });
      }

    if (!value.acceptLegal) {
        ctx.addIssue({
          code: "custom",
          path: ["acceptLegal"],
          message:
            "Bitte akzeptieren Sie die Nutzungsbedingungen und nehmen Sie die Datenschutzerklärung zur Kenntnis.",
        });
    }

      if (!requiresPerson) {
        return;
      }

      if (value.person.displayName.trim().length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["person", "displayName"],
          message: "Der Anzeigename ist erforderlich.",
        });
      }

      const phone = emptyStringToUndefined(value.person.mobilePhoneE164);

      if (phone !== undefined && !isLikelyPhoneE164(phone)) {
        ctx.addIssue({
          code: "custom",
          path: ["person", "mobilePhoneE164"],
          message:
            "Bitte verwenden Sie das internationale Format, zum Beispiel +491701234567.",
        });
      }
    })
    .transform((value) => ({
      password: value.password,
      acceptedUserTermsVersion: legalVersions.acceptedUserTermsVersion,
      acceptedPrivacyVersion: legalVersions.acceptedPrivacyVersion,
      person: requiresPerson
        ? {
            displayName: value.person.displayName.trim(),
            preferredName: emptyStringToUndefined(value.person.preferredName),
            mobilePhoneE164: emptyStringToUndefined(
              value.person.mobilePhoneE164,
            ),
            canAppearInPhoto: value.person.canAppearInPhoto,
          }
        : null,
    }));
}

export type AcceptInvitationValidationResult = z.infer<
  ReturnType<typeof buildAcceptInvitationSchema>
>;
